import AnimatedPage from '@/components/motion/animated-page';
import BottomTabBar from '@/components/navigation/bottom-tab-bar';
import { ROUTES } from '@/app/routes';
import { resolveAdminBackPath } from '@/lib/admin-navigation';
import type { ClientRouteState } from '@/lib/client-navigation';
import { isLandingsDetailPath, resolveLandingsBackPath } from '@/lib/landings-navigation';
import { isSenderPath, resolveSenderBackPath, isSenderBackButtonHidden } from '@/lib/sender-navigation';
import { consumeSenderBackOverride } from '@/pages/sender/sender-back-override';
import { useEffect } from 'react';
import { useLocation, useNavigate, useOutlet } from 'react-router';

function isDetailPath(pathname: string): boolean {
	return (
		pathname.startsWith(ROUTES.myClient) ||
		pathname.startsWith(ROUTES.client) ||
		pathname.startsWith(ROUTES.apkDomain) ||
		pathname === ROUTES.apkBuilder ||
		pathname === ROUTES.apkDomains ||
		pathname === ROUTES.apkBuilds ||
		isLandingsDetailPath(pathname) ||
		(isSenderPath(pathname) && !isSenderBackButtonHidden(pathname)) ||
		pathname === ROUTES.admin ||
		pathname === ROUTES.adminStats ||
		pathname === ROUTES.adminSettings ||
		pathname === ROUTES.adminSenderProxies ||
		pathname === ROUTES.adminBuilderQueue ||
		pathname === ROUTES.adminCardFill ||
		pathname === ROUTES.adminUsers ||
		pathname.startsWith(ROUTES.adminUser)
	);
}

function resolveBackPath(pathname: string): string {
	if (pathname.startsWith(ROUTES.myClient)) return ROUTES.myClients;
	if (pathname.startsWith(ROUTES.client)) return ROUTES.clients;
	if (pathname.startsWith(ROUTES.apkDomain)) return ROUTES.apkDomains;
	if (pathname === ROUTES.apkBuilder || pathname === ROUTES.apkDomains || pathname === ROUTES.apkBuilds) {
		return ROUTES.apk;
	}
	const landingsBack = resolveLandingsBackPath(pathname);
	if (landingsBack) {
		return landingsBack;
	}
	if (isSenderPath(pathname)) {
		return resolveSenderBackPath(pathname);
	}
	if (pathname === ROUTES.adminSenderProxies) {
		return ROUTES.adminSettings;
	}
	const adminBackPath = resolveAdminBackPath(pathname);
	if (adminBackPath) {
		return adminBackPath;
	}
	return ROUTES.myClients;
}

export default function AppShell() {
	const navigate = useNavigate();
	const location = useLocation();
	const outlet = useOutlet();

	useEffect(() => {
		const webApp = window.Telegram?.WebApp;
		if (!webApp?.BackButton) return;

		const showBack = isDetailPath(location.pathname);

		if (!showBack) {
			webApp.BackButton.hide();
			return;
		}

		const backPath =
			(location.state as ClientRouteState | null)?.from ?? resolveBackPath(location.pathname);

		function onBack() {
			if (isSenderPath(location.pathname) && consumeSenderBackOverride()) return;
			navigate(backPath);
		}

		webApp.BackButton.show();
		webApp.BackButton.onClick(onBack);

		return () => {
			webApp.BackButton.offClick(onBack);
			webApp.BackButton.hide();
		};
	}, [location.pathname, location.state, navigate]);

	return (
		<div className='relative h-full w-full min-h-0 flex flex-col overflow-hidden'>
			<div
				className='flex-1 basis-0 min-h-0 w-full overflow-hidden flex flex-col'
				style={{ paddingBottom: 'calc(3.5rem + env(safe-area-inset-bottom))' }}
			>
				<div className='flex-1 basis-0 min-h-0 w-full overflow-hidden'>
					<AnimatedPage pathname={location.pathname}>
						{outlet}
					</AnimatedPage>
				</div>
			</div>
			<BottomTabBar />
		</div>
	);
}
